type ThreeModule = typeof import('three');

export type ProformGlassBackground = 'none' | 'logo' | 'flowers';

type PreviewCamera = import('three').PerspectiveCamera | import('three').OrthographicCamera;

export type ProformGlassBackdrop = {
  aspectRatio?: number;
  attachedScene?: import('three').Scene;
  image: HTMLImageElement;
  kind: Exclude<ProformGlassBackground, 'none'>;
  previousSceneBackground?: import('three').Scene['background'];
  texture: import('three').CanvasTexture;
};

export function updateProformGlassBackdrop(backdrop: ProformGlassBackdrop, camera: PreviewCamera) {
  // The texture is installed as scene.background, which Three renders before
  // opaque objects in both the main and MeshPhysicalMaterial transmission
  // passes. Keeping it out of the far clipping plane avoids intermittent
  // framebuffer gaps while the product or camera moves.
  const projection = camera.projectionMatrix.elements;
  const width = 2 / Math.abs(projection[0]);
  const height = 2 / Math.abs(projection[5]);

  // Camera movement does not change a scene background's screen-space image.
  // Round away projection noise from dolly/zoom before checking the cache.
  const aspectRatio = Math.round((width / height) * 1e8) / 1e8;
  if (backdrop.aspectRatio === aspectRatio) return;

  const canvas = backdrop.texture.image as HTMLCanvasElement;
  // Keep GPU texture dimensions stable when resizing the viewport. Fit the
  // image in screen proportions, then convert to the square texture's UVs.
  canvas.width = 1536;
  canvas.height = 1536;
  const context = canvas.getContext('2d');
  if (!context) return;
  context.fillStyle = backdrop.kind === 'logo' ? '#f8f7f4' : '#d5d3d1';
  context.fillRect(0, 0, canvas.width, canvas.height);
  const imageScale =
    backdrop.kind === 'logo'
      ? Math.min((width * 0.9) / backdrop.image.width, (height * 0.9) / backdrop.image.height)
      : Math.max(width / backdrop.image.width, height / backdrop.image.height);
  const imageWidth = ((backdrop.image.width * imageScale) / width) * canvas.width;
  const imageHeight = ((backdrop.image.height * imageScale) / height) * canvas.height;
  context.drawImage(
    backdrop.image,
    (canvas.width - imageWidth) / 2,
    (canvas.height - imageHeight) / 2,
    imageWidth,
    imageHeight,
  );
  backdrop.texture.needsUpdate = true;
  backdrop.aspectRatio = aspectRatio;
}

export async function createProformGlassBackdrop(
  THREE: ThreeModule,
  kind: Exclude<ProformGlassBackground, 'none'>,
  camera: PreviewCamera,
): Promise<ProformGlassBackdrop> {
  const image = await new THREE.ImageLoader().loadAsync(
    kind === 'logo' ? '/images/logo.svg' : '/images/configurator/glass-flowers.png',
  );
  const texture = new THREE.CanvasTexture(document.createElement('canvas'));
  texture.colorSpace = THREE.SRGBColorSpace;
  const backdrop = { image, kind, texture };
  updateProformGlassBackdrop(backdrop, camera);
  return backdrop;
}

export function attachProformGlassBackdrop(
  backdrop: ProformGlassBackdrop,
  scene: import('three').Scene,
) {
  if (backdrop.attachedScene === scene && scene.background === backdrop.texture) return;
  detachProformGlassBackdrop(backdrop);
  backdrop.attachedScene = scene;
  backdrop.previousSceneBackground = scene.background;
  scene.background = backdrop.texture;
}

export function detachProformGlassBackdrop(backdrop: ProformGlassBackdrop) {
  const scene = backdrop.attachedScene;
  if (scene?.background === backdrop.texture) {
    scene.background = backdrop.previousSceneBackground ?? null;
  }
  backdrop.attachedScene = undefined;
  backdrop.previousSceneBackground = undefined;
}

export function disposeProformGlassBackdrop(backdrop: ProformGlassBackdrop) {
  detachProformGlassBackdrop(backdrop);
  backdrop.texture.dispose();
}
